import Button from 'ext-ui/components/Button/Button'
import CardsNewIcon from 'ext-ui/svg/icon/CardsNew'
import ChevronIcon from 'ext-ui/svg/icon/ChevronIcon'
import React, { ReactElement, useState } from 'react'
import MaskedCardNumber from 'src/components/MaskedCardNumber/MaskedCardNumber'
import PluralTrns from 'src/localization/PluralTrns'
import { useTranslate } from 'src/localization/Trns'
import { CardFeedCard } from 'src/queries/cardFeeds/cardFeeds.models'

import * as style from './AssignmentCardList.style'

export interface AssignmentCardListProps {
  cards: CardFeedCard[]
  /** Rows shown before "See all" reveals the rest. */
  collapsedCount?: number
}

/**
 * The "ASSIGNING ACCESS TO" card list. The header chevron collapses the whole
 * list; "See all" expands the row set into a scrollable region so a long
 * selection doesn't push the modal's actions off-screen.
 */
const AssignmentCardList: React.FC<AssignmentCardListProps> = ({
  cards,
  collapsedCount = 3,
}): ReactElement => {
  const translate = useTranslate()
  const [listOpen, setListOpen] = useState(true)
  const [showAll, setShowAll] = useState(false)

  const hasOverflow = cards.length > collapsedCount
  const visibleCards = showAll ? cards : cards.slice(0, collapsedCount)

  return (
    <div css={style.container}>
      <div css={style.header}>
        <span css={style.caption}>
          {translate('Assigning access to')}{' '}
          <span css={style.captionCount}>
            <PluralTrns
              singleSource={'(1 card selected)'}
              pluralSource={'(${count} cards selected)'}
              count={cards.length}
            />
          </span>
        </span>
        <Button
          variant="no-shell"
          css={style.chevronButton}
          aria-expanded={listOpen}
          aria-label={translate('Toggle card list')}
          onClick={(): void => setListOpen((open) => !open)}
        >
          <ChevronIcon direction={listOpen ? 'down' : 'up'} />
        </Button>
      </div>
      {listOpen && (
        <>
          <ul css={showAll ? style.rowsScrollable : style.rows}>
            {visibleCards.map((card) => (
              <li key={card.id} css={style.row}>
                <span css={style.cardIdentity}>
                  <CardsNewIcon aria-hidden="true" />
                  <span css={style.cardName}>{card.cardName}</span>
                </span>
                <MaskedCardNumber lastFour={card.lastFour} />
              </li>
            ))}
          </ul>
          {hasOverflow && (
            <Button
              variant="no-shell"
              css={style.seeAllButton}
              onClick={(): void => setShowAll((all) => !all)}
            >
              {showAll ? translate('See less') : translate('See all')}
            </Button>
          )}
        </>
      )}
    </div>
  )
}

export default AssignmentCardList
